import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  api, ApiError,
  type Farm, type Master, type LorawanDevice,
} from "../api";

/** Server already computes is_online (last_seen within 24h); this just
 *  renders the badge. "Chưa nghe" is a third state distinct from offline
 *  — never-uplinked devices haven't joined yet (might be a key/region
 *  mismatch), worth visually separating from a long-silent device. */
function renderStatus(d: LorawanDevice): JSX.Element {
  if (!d.last_seen_at) {
    return <span className="badge" style={{ background: "#f3f4f6", color: "#6b7280" }}>chưa nghe</span>;
  }
  if (d.is_online) {
    return <span className="badge owner">online</span>;
  }
  return <span className="badge" style={{ background: "#fef3c7", color: "#92400e" }}>offline</span>;
}

/** "5 phút trước" / "2 giờ trước" / "3 ngày trước" / absolute date for older.
 *  Better than the raw ISO string the API returns — operators care about
 *  recency relative to now, not wall-clock down to the second. */
function formatLastSeen(iso: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (isNaN(ts)) return "—";
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 60) return `${diffSec}s trước`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} phút trước`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} giờ trước`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} ngày trước`;
  return new Date(iso).toLocaleString();
}

/** Settings → LoRaWAN devices.
 *
 * Overview-style: one section per master, all loaded in parallel. Operator
 * sees every farm's MCU fleet at a glance — no picker to scroll past. The
 * Add Device modal asks which master to enroll under, since masters are
 * still the routing key (ChirpStack tags `irrigation_master_id` on each
 * device → lorawan_tenant_bridge uses it to fan-out uplinks to the right
 * master_backend). */
export function LorawanDevicesPage() {
  const [farms, setFarms] = useState<Farm[] | null>(null);
  const [masters, setMasters] = useState<Master[] | null>(null);
  // Per-master device map: master_id → list-or-null (null = still loading)
  const [devicesByMaster, setDevicesByMaster] = useState<Record<string, LorawanDevice[] | null>>({});
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [showAddForMaster, setShowAddForMaster] = useState<string | null>(null);
  const [busyEui, setBusyEui] = useState<string | null>(null);
  // Rename target = { masterId, devEui, currentName } — null when no
  // rename modal is open. One nullable state instead of three so we
  // don't have to keep them in sync when opening / closing.
  const [renameTarget, setRenameTarget] = useState<{
    masterId: string; devEui: string; currentName: string;
  } | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Farm[]>("/farms"),
      api.get<Master[]>("/masters"),
    ]).then(([f, m]) => {
      setFarms(f);
      setMasters(m);
      // Fan out — one /devices?master_id=X call per master, in parallel.
      // ChirpStack list calls are ~100ms each; serial would scale linearly
      // with farm count, parallel keeps page-load constant under N≈20.
      m.forEach(master => fetchDevices(master.id));
    }).catch(e => setLoadErr((e as Error).message));
  }, []);

  async function fetchDevices(masterId: string) {
    try {
      const list = await api.get<LorawanDevice[]>(
        `/lorawan/devices?master_id=${masterId}`,
      );
      setDevicesByMaster(prev => ({ ...prev, [masterId]: list }));
    } catch (e) {
      setDevicesByMaster(prev => ({ ...prev, [masterId]: [] }));
      setLoadErr((e as Error).message);
    }
  }

  async function handleDelete(masterId: string, devEui: string) {
    if (!confirm(`Xóa thiết bị ${devEui}? ChirpStack sẽ ngưng chấp nhận uplink từ DevEUI này.`)) return;
    setBusyEui(devEui);
    try {
      await api.del(`/lorawan/devices/${devEui}?master_id=${masterId}`);
      await fetchDevices(masterId);
    } catch (e) {
      alert(`Xóa thất bại: ${(e as Error).message}`);
    } finally {
      setBusyEui(null);
    }
  }

  async function handleToggleDisabled(masterId: string, devEui: string, currentlyDisabled: boolean) {
    setBusyEui(devEui);
    try {
      await api.post(`/lorawan/devices/${devEui}/disabled`, {
        master_id: masterId, disabled: !currentlyDisabled,
      });
      await fetchDevices(masterId);
    } catch (e) {
      alert(`Thao tác thất bại: ${(e as Error).message}`);
    } finally {
      setBusyEui(null);
    }
  }

  async function handleRename(masterId: string, devEui: string, newName: string) {
    setBusyEui(devEui);
    try {
      await api.post(`/lorawan/devices/${devEui}/rename`, {
        master_id: masterId, name: newName,
      });
      await fetchDevices(masterId);
      setRenameTarget(null);
    } catch (e) {
      // Modal stays open so the operator can correct + retry. Surfacing
      // via alert keeps consistency with the other handlers.
      alert(`Đổi tên thất bại: ${(e as Error).message}`);
    } finally {
      setBusyEui(null);
    }
  }

  const farmById = useMemo(() => {
    const m = new Map<string, Farm>();
    (farms ?? []).forEach(f => m.set(f.id, f));
    return m;
  }, [farms]);

  const isLoading = farms === null || masters === null;

  return (
    <>
      <div className="row" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>LoRaWAN Devices</h1>
      </div>

      {loadErr && <div className="alert error">{loadErr}</div>}

      {isLoading && <div className="muted">Đang tải…</div>}

      {!isLoading && masters?.length === 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Chưa có master nào. Tạo farm + đăng ký master ở Settings → Farms trước, rồi quay lại đây thêm MCU.
          </p>
        </div>
      )}

      {(masters ?? []).map(master => {
        const farmName = farmById.get(master.farm_id)?.name || "?";
        const devices = devicesByMaster[master.id];
        return (
          <MasterSection
            key={master.id}
            masterLabel={master.label || master.id}
            farmName={farmName}
            devices={devices}
            onAdd={() => setShowAddForMaster(master.id)}
            onDelete={(eui) => handleDelete(master.id, eui)}
            onToggleDisabled={(eui, dis) => handleToggleDisabled(master.id, eui, dis)}
            onRename={(eui, currentName) => setRenameTarget({
              masterId: master.id, devEui: eui, currentName,
            })}
            busyEui={busyEui}
          />
        );
      })}

      {showAddForMaster && (
        <AddDeviceModal
          masterId={showAddForMaster}
          onClose={() => setShowAddForMaster(null)}
          onCreated={() => {
            const m = showAddForMaster;
            setShowAddForMaster(null);
            if (m) fetchDevices(m);
          }}
        />
      )}

      {renameTarget && (
        <RenameDeviceModal
          devEui={renameTarget.devEui}
          currentName={renameTarget.currentName}
          busy={busyEui === renameTarget.devEui}
          onClose={() => setRenameTarget(null)}
          onSubmit={(newName) => handleRename(
            renameTarget.masterId, renameTarget.devEui, newName,
          )}
        />
      )}
    </>
  );
}

function MasterSection({
  masterLabel, farmName, devices, onAdd, onDelete, onToggleDisabled, onRename, busyEui,
}: {
  masterLabel: string;
  farmName: string;
  devices: LorawanDevice[] | null | undefined;
  onAdd: () => void;
  onDelete: (devEui: string) => void;
  onToggleDisabled: (devEui: string, currentlyDisabled: boolean) => void;
  onRename: (devEui: string, currentName: string) => void;
  busyEui: string | null;
}) {
  return (
    <div className="card" style={{ padding: 0, marginBottom: 16 }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "1rem", fontWeight: 600 }}>{masterLabel}</div>
          <div className="muted" style={{ fontSize: 12 }}>@ {farmName}</div>
        </div>
        <button className="primary small" onClick={onAdd}>+ Add Device</button>
      </div>

      {devices === undefined || devices === null ? (
        <div className="muted" style={{ padding: 16 }}>Đang tải…</div>
      ) : devices.length === 0 ? (
        <div className="muted" style={{ padding: 16 }}>Master này chưa có MCU LoRa nào.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 12px" }}>DevEUI</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Tên</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Class</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Trạng thái</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Last Seen</th>
              <th style={{ width: 260, padding: "8px 12px" }}></th>
            </tr>
          </thead>
          <tbody>
            {devices.map(d => (
              <tr key={d.dev_eui} style={{ borderTop: "1px solid #eee", opacity: d.is_disabled ? 0.55 : 1 }}>
                <td style={{ padding: "8px 12px", fontFamily: "monospace" }}>{d.dev_eui}</td>
                <td style={{ padding: "8px 4px" }}>{d.name}</td>
                <td style={{ padding: "8px 4px" }}>
                  {d.device_class
                    ? <span className="badge" style={{ background: "#e0e7ff", color: "#3730a3" }}>Class {d.device_class}</span>
                    : <span className="muted">—</span>}
                </td>
                <td style={{ padding: "8px 4px" }}>
                  {d.is_disabled
                    ? <span className="badge" style={{ background: "#fee2e2", color: "#991b1b" }}>disabled</span>
                    : renderStatus(d)}
                </td>
                <td style={{ padding: "8px 4px" }}>{formatLastSeen(d.last_seen_at)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>
                  <div style={{ display: "inline-flex", gap: 6 }}>
                    <button
                      className="small"
                      disabled={busyEui === d.dev_eui}
                      onClick={() => onRename(d.dev_eui, d.name)}
                      title="Đổi tên — chỉ là label, không ảnh hưởng join/keys/routing"
                    >
                      Đổi tên
                    </button>
                    <button
                      className="small"
                      disabled={busyEui === d.dev_eui}
                      onClick={() => onToggleDisabled(d.dev_eui, d.is_disabled)}
                      title={d.is_disabled
                        ? "Bật lại — ChirpStack tiếp tục accept uplink (không cần re-join)"
                        : "Vô hiệu hóa — ChirpStack drop uplink, giữ session keys"}
                    >
                      {busyEui === d.dev_eui ? "…" : (d.is_disabled ? "Enable" : "Disable")}
                    </button>
                    <button
                      className="danger small"
                      disabled={busyEui === d.dev_eui}
                      onClick={() => onDelete(d.dev_eui)}
                    >
                      Xóa
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function AddDeviceModal({ masterId, onClose, onCreated }: {
  masterId: string; onClose: () => void; onCreated: () => void;
}) {
  const [devEui, setDevEui] = useState("");
  const [appKey, setAppKey] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.post("/lorawan/devices", {
        dev_eui: devEui.trim(),
        app_key: appKey.trim(),
        name: name.trim(),
        master_id: masterId,
      });
      onCreated();
    } catch (e) {
      const m = e instanceof ApiError ? (typeof (e.body as any)?.detail === "string" ? (e.body as any).detail : e.message) : (e as Error).message;
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Add LoRaWAN Device</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          DevEUI và AppKey phải khớp với firmware đã flash trên MCU.
          ChirpStack sẽ tag device với master_id để uplink tự động chảy về master này.
        </p>
        {err && <div className="alert error" style={{ marginBottom: 12 }}>{err}</div>}
        <form onSubmit={submit}>
          <label>Tên</label>
          <input
            value={name} onChange={e => setName(e.target.value)} required
            placeholder="vd outdoor-mcu-01" />
          <label>DevEUI (16 hex)</label>
          <input
            value={devEui} onChange={e => setDevEui(e.target.value)} required
            pattern="[0-9a-fA-F:\- ]{16,}"
            style={{ fontFamily: "monospace" }}
            placeholder="0000000000000020" />
          <label>AppKey (32 hex)</label>
          <input
            value={appKey} onChange={e => setAppKey(e.target.value)} required
            pattern="[0-9a-fA-F:\- ]{32,}"
            style={{ fontFamily: "monospace" }}
            placeholder="88888888888888888888888888888820" />
          <div className="row" style={{ marginTop: 16 }}>
            <button type="button" onClick={onClose} disabled={busy}>Hủy</button>
            <div className="spacer" />
            <button className="primary" type="submit" disabled={busy}>
              {busy ? "Đang tạo…" : "Tạo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RenameDeviceModal({ devEui, currentName, busy, onClose, onSubmit }: {
  devEui: string; currentName: string; busy: boolean;
  onClose: () => void; onSubmit: (newName: string) => void;
}) {
  const [name, setName] = useState(currentName);

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentName) return;
    onSubmit(trimmed);
  }

  const dirty = name.trim() !== "" && name.trim() !== currentName;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Đổi tên thiết bị</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          DevEUI <code style={{ fontFamily: "monospace" }}>{devEui}</code>.
          Đổi tên chỉ cập nhật label trong ChirpStack; không ảnh hưởng OTAA join,
          session keys, hay routing tags.
        </p>
        <form onSubmit={submit}>
          <label>Tên mới</label>
          <input
            value={name} onChange={e => setName(e.target.value)}
            autoFocus required maxLength={100}
            placeholder={currentName} />
          <div className="row" style={{ marginTop: 16 }}>
            <button type="button" onClick={onClose} disabled={busy}>Hủy</button>
            <div className="spacer" />
            <button className="primary" type="submit" disabled={busy || !dirty}>
              {busy ? "Đang đổi…" : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}